// The Trace tab's timeline: one row per step, production and the new version side by side.
// Rows are the tool calls NinjaEval recorded (every run has those, so both versions are always comparable) and the reply.
// What happened inside the service before each step (LLM calls, retrievals, …) hangs off that step: from the Langfuse
// trace when there is one, else from the bot's own record (the ninjaeval block).

const text = (v) => (typeof v === 'string' ? v : v == null ? '' : JSON.stringify(v));
export const failed = (x) => !!x && (x.level === 'ERROR' || !!x.error || /^\s*("?error|\{"error)/i.test(text(x.output)));
const sameArgs = (a, b) => JSON.stringify(a || {}) === JSON.stringify(b || {});

// One run → { source, turns, steps: [{ name, input, output, error, judge, span, inner: [...], turn }, …] }
// A step per tool call, then a reply step, for each customer message: a single-message case has one turn, a multi-turn
// case one per message (from ex.conversation; tool calls carry cust_turn).
export function stepsOf(ex, lf) {
  if (!ex) return null;
  const judge = ex.judge?.steps || [];
  const tj = judge.filter((s) => s.step_type !== 'llm_call');
  const conv = ex.conversation || [];
  const n = Math.max(1, conv.length);
  const replyJudge = (t) => (n === 1 ? judge.find((s) => s.step_type === 'llm_call')
    : judge.find((s) => s.step_type === 'llm_call' && s.name === (t === n ? 'reply' : `reply (turn ${t})`)));
  const calls = (ex.tool_calls || []).map((c, i) => ({ name: c.name, input: c.input ?? c.arguments, output: c.output ?? c.result,
    error: c.error, judge: tj[i], inner: [], turn: c.cust_turn || 1,
    agent: c.parent && c.agent ? c.agent : null, attempt: c.attempt }));   // contract v1.1: a sub-agent (one with a parent) made it
  const steps = [];
  for (let t = 1; t <= n; t++) {
    steps.push(...calls.filter((c) => c.turn === t));
    const said = conv[t - 1];
    steps.push({ name: 'reply', reply: true, turn: t, message: said?.message, inner: [], judge: replyJudge(t),
      output: said ? said.reply : ex.error ? null : ex.output, error: t === n ? ex.error : null });
  }
  const firstOf = (t) => steps.findIndex((x) => x.turn === t);
  const replyOf = (t) => steps.findIndex((x) => x.reply && x.turn === t);
  let source = 'basic';
  const turns = ex.turns || [];
  if (lf?.found && lf.steps?.length) {
    source = 'langfuse';
    let k = 0, roots = 0;                             // the step the next span leads up to; each request is a root span
    const byId = Object.fromEntries(lf.steps.filter((x) => x.id).map((x) => [x.id, x]));
    const parents = new Set(lf.steps.map((x) => x.parent).filter(Boolean));
    const agentOf = (x) => {                          // the nearest sub-agent above a span (not the root agent)
      for (let p = byId[x.parent], m = 0; p && p.depth && m < 20; p = byId[p.parent], m++) if (p.type === 'agent') return p.name || 'agent';
      return null;
    };
    for (const s of lf.steps) {
      if (!s.depth) {                                 // the next customer message's request starts a new root
        if (++roots > 1 && n > 1 && firstOf(roots) >= 0) k = firstOf(roots);
        continue;
      }
      if (s.type === 'agent' && parents.has(s.id)) continue;   // a sub-agent shows as the group around its own steps
      const node = { kind: s.type, name: s.name || s.type, model: s.model, ms: s.latency_ms, input: s.input, output: s.output, level: s.level, tools: s.tools,
        agent: agentOf(s) };
      let at = -1;
      if (s.type === 'tool') for (let i = k; i < steps.length; i++) if (!steps[i].reply && steps[i].name === s.name) { at = i; break; }
      if (at >= 0) { steps[at].span = node; steps[at].agent = node.agent; k = at + 1; } else (steps[k] || steps[steps.length - 1]).inner.push(node);
    }
  } else if (turns.length > 1 || turns[0]?.prompt_tokens != null) {
    source = 'record';
    let k = 0, cur = 1;                               // each LLM turn comes before the calls it asked for; the last one wrote the reply
    for (const t of turns) {
      if ((t.cust_turn || 1) !== cur && firstOf(t.cust_turn) >= 0) { cur = t.cust_turn; k = firstOf(cur); }
      steps[k].inner.push({ kind: 'generation', name: 'LLM call', model: t.model, ms: t.latency_ms, output: t.output,
        tokens: (t.prompt_tokens || 0) + (t.completion_tokens || 0) || null, messages: t.messages, messages_total: t.messages_total,
        params: t.params, version: t.model_version, attempt: t.attempt, wait: t.wait_ms, agent: t.parent && t.agent ? t.agent : null,
        cost: t.cost_usd });
      const end = replyOf(cur);
      k = Math.min(end >= 0 ? end : steps.length - 1, k + (t.tool_calls?.length || 0));
    }
  }
  pinEvidence(ex, steps);
  return { source, turns: n, steps };
}

// Put each failed check and tool-call mismatch on the step it's about, and mark a tool call that repeats an earlier
// identical one: a score (or a loop) should point back to the step that produced it.
function pinEvidence(ex, steps) {
  const tools = steps.filter((s) => !s.reply);
  const lastReply = [...steps].reverse().find((s) => s.reply);
  const add = (s, e) => { if (s) (s.evidence = s.evidence || []).push(e); };
  const firstNamed = (name) => tools.find((s) => s.name === name);
  for (const c of ex.checks_result || []) {
    if (c.passed) continue;
    const k = c.check || {};
    if (k.type === 'not_calls_tool') tools.filter((s) => s.name === k.tool).forEach((s) => add(s, `check failed: ${c.label}`));
    else add(lastReply, `check failed: ${c.label}${c.detail ? ` (${c.detail})` : ''}`);
  }
  for (const f of (ex.comparison?.fields || []).filter((x) => x.result !== 'PASS')) {
    const name = String(f.field || '').replace(/^tool\s+/, '').split('.')[0];
    add(firstNamed(name) || lastReply, `expected ${f.field}: ${show(f.expected)}, got ${show(f.actual)}`);
  }
  const seen = new Map();
  tools.forEach((s) => { const key = `${s.name}|${JSON.stringify(s.input || {})}`; if (seen.has(key)) s.repeatOf = seen.get(key); else seen.set(key, steps.indexOf(s) + 1); });
}
const show = (v) => (v == null ? 'nothing' : typeof v === 'object' ? JSON.stringify(v).slice(0, 80) : String(v).slice(0, 80));

// Line the two runs up turn by turn; within a turn, tool calls by name (longest common subsequence), so one extra call
// doesn't shift every row. Each turn ends with its reply row.
export function align(a, b) {
  const n = Math.max(a?.turns || 1, b?.turns || 1);
  const rows = [];
  for (let t = 1; t <= n; t++) {
    const x = (a?.steps || []).filter((s) => !s.reply && s.turn === t), y = (b?.steps || []).filter((s) => !s.reply && s.turn === t);
    const L = Array.from({ length: x.length + 1 }, () => new Array(y.length + 1).fill(0));
    for (let i = x.length - 1; i >= 0; i--) for (let j = y.length - 1; j >= 0; j--)
      L[i][j] = x[i].name === y[j].name ? L[i + 1][j + 1] + 1 : Math.max(L[i + 1][j], L[i][j + 1]);
    let i = 0, j = 0;
    while (i < x.length || j < y.length) {
      if (i < x.length && j < y.length && x[i].name === y[j].name) rows.push([x[i++], y[j++], t]);
      else if (j >= y.length || (i < x.length && L[i + 1][j] >= L[i][j + 1])) rows.push([x[i++], null, t]);
      else rows.push([null, y[j++], t]);
    }
    const reply = (j2) => (j2?.steps || []).find((s) => s.reply && s.turn === t) || null;
    rows.push([reply(a), reply(b), t, true]);
  }
  return rows.map(([p, q, turn, isReply]) => ({ a: p, b: q, turn, change: isReply && (!p || !q) ? 'noreply' : change(p, q) }));
}

// The arguments two calls of the same tool disagree on: [[name, first, second], …]
export function argDiff(a, b) {
  const x = a || {}, y = b || {};
  return [...new Set([...Object.keys(x), ...Object.keys(y)])].filter((k) => JSON.stringify(x[k]) !== JSON.stringify(y[k])).map((k) => [k, x[k], y[k]]);
}

// What differs in one row, or null when it's the same.
export function change(p, q) {
  if (p?.reply || q?.reply) {
    if (!!p?.error !== !!q?.error) return q?.error ? 'errored' : 'recovered';
    const s = p?.judge?.score, t = q?.judge?.score;
    return s != null && t != null && Math.abs(s - t) >= 0.2 ? 'reply' : null;
  }
  if (!p) return 'added';
  if (!q) return 'missing';
  if (failed(p) !== failed(q)) return failed(q) ? 'errored' : 'recovered';
  if ((p.agent || null) !== (q.agent || null)) return 'agent';
  return sameArgs(p.input, q.input) ? null : 'args';
}

// The Map view: one mind map per case. The customer's message is the root, each version a branch (for a multi-turn case,
// then a node per customer message), then its steps,
// what happened inside the service before each, and the reply. Node content is HTML, so every bit of text is escaped
// (tool results and replies come from the bot). `fmt` shortens a tool result; changed steps carry payload.diff.
const esc = (v) => text(v).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
const cut = (v, n) => { const s = text(v).replace(/\s+/g, ' ').trim(); return s.length > n ? s.slice(0, n) + '…' : s; };
const ICONS = { generation: '🧠', tool: '🔧', retriever: '📚', guardrail: '🛡', span: '▫', agent: '🤖', chain: '⛓', embedding: '🔢', evaluator: '⚖', event: '•' };
const score = (j) => (j?.score != null ? ` <code>${Number(j.score).toFixed(2)}</code>` : '');

// Red marks only where the new version went wrong (`defects`: step → why, from defectsOf), and the path down to it;
// other differences keep their version's colour with a ≠. `reg(x)` gives a step or inner call an id, so a click on its
// node can open the full data behind it.
export function mapOf({ query, messages, sides, rows, fmt = cut, first, defects = new Map(), reg = () => null }) {
  const changed = new Set(rows.filter((x) => x.change).flatMap((x) => [x.a, x.b]).filter(Boolean));
  // the critical point: the earliest step where the new version went wrong (it gets the 斬! burst); `first`
  // ({step, note}) is where it first differs, which may be harmless (payload.burst, shown as a quiet ≠ marker)
  const critical = criticalOf(rows, defects);
  const pay = (s, diff) => ({ ...(diff ? { diff: true } : {}), ...(defects.has(s) ? { defect: esc(cut(defects.get(s), 80)) } : {}),
    ...(s === critical ? { critical: true } : {}), ...(first && s === first.step ? { burst: esc(first.note) } : {}), ref: reg(s) });
  const leaf = (content, flags = {}) => ({ content, children: [], payload: flags });
  const inner = (n, bad) => leaf(`${n.agent ? `<i>${esc(n.agent)}</i> › ` : ''}${ICONS[n.kind] || '▫'} ${esc(n.name)}${n.model ? ` · ${esc(n.model.split('/').pop())}` : ''}${n.ms != null ? ` · ${(n.ms / 1000).toFixed(1)}s` : ''}${failed(n) ? ' · <b>error</b>' : ''}`,
    { ref: reg(n), ...(bad ? { inDefect: true } : {}) });
  const step = (s) => {
    const diff = changed.has(s), bad = defects.has(s), f = bad ? { inDefect: true } : {};
    const kids = s.inner.map((n) => inner(n, bad));
    if (s.reply) {
      kids.push(leaf(s.error ? `<b>error</b> ${esc(cut(s.error, 80))}` : esc(cut(s.output, 90)) || '—', { ...f, ref: reg(s) }));
      return { content: `💬 <b>reply</b>${score(s.judge)}${diff ? ' ≠' : ''}`, children: kids, payload: pay(s, diff) };
    }
    const args = Object.entries(s.input || {}).map(([k, v]) => `${k}=${cut(v, 24)}`).join(', ');
    if (args) kids.push(leaf(`<code>${esc(cut(args, 70))}</code>`, { ...f, ref: reg(s) }));
    kids.push(leaf(`→ ${esc(fmt(s.output, 60))}`, { ...f, ref: reg(s) }));
    return { content: `🔧 <b>${esc(s.name)}</b>${score(s.judge)}${diff ? ' ≠' : ''}`, children: kids, payload: pay(s, diff) };
  };
  const grouped = (all) => {                          // consecutive steps of one sub-agent sit under that agent's node
    const out = [];
    for (const s of all) {
      const last = out[out.length - 1];
      if (!s.agent) out.push(step(s));
      else if (last?.agent === s.agent) last.children.push(step(s));
      else out.push({ content: `🤖 <b>${esc(s.agent)}</b>`, agent: s.agent, children: [step(s)], payload: {} });
      if (s.agent && defects.has(s)) out[out.length - 1].payload.path = true;
    }
    return out;
  };
  const tag = (n, side) => { n.payload.side = side; n.children.forEach((c) => tag(c, side)); return n; };   // colours each version
  const multi = (messages || []).length > 1;
  const byTurn = (j) => messages.map((m, t) => {      // multi-turn: what the customer said, then that turn's steps and reply
    const own = j.steps.filter((s) => s.turn === t + 1);
    return { content: `👤 <b>${t + 1}.</b> ${esc(cut(m, 60))}`, children: own.length ? grouped(own) : [leaf('no reply: the conversation stopped')],
      payload: own.some((s) => defects.has(s)) ? { path: true } : {} };
  });
  return { content: multi ? `👤 <b>${messages.length} messages</b> · ${esc(cut(query, 50))}` : `👤 ${esc(cut(query, 70))}`, payload: {},
    children: sides.map(({ label, j, ex }, i) => tag({
      content: `🤖 <b>${esc(label)}</b>${ex?.latency_ms != null ? ` · ${(ex.latency_ms / 1000).toFixed(1)}s` : ''}`,
      payload: (j?.steps || []).some((s) => defects.has(s)) ? { path: true } : {},
      children: !j ? [leaf('no run')] : multi ? byTurn(j) : grouped(j.steps) }, i)) };
}

// Where the new version went wrong, for a case it doesn't pass: step → why. The steps a failed check or a tool-call
// mismatch points at, tool calls that errored only on the new version, a reply the judge scored low; else the first
// difference (a call it didn't make counts against that turn's reply). Production is the reference, never a defect.
export function defectsOf(rows, cand, why, cause) {
  const out = new Map();
  if (!cand || (cand.label === 'PASS' && cand.status !== 'error')) return out;
  const replyOf = (t) => rows.find((x) => x.turn === t && x.b?.reply)?.b;
  rows.forEach((x) => {
    const s = x.b;
    if (s?.evidence?.length) out.set(s, s.evidence[0]);
    else if (s && x.change === 'errored') out.set(s, s.reply ? 'no answer: the run errored' : 'this call errored');
  });
  const last = [...rows].reverse().find((x) => x.b?.reply)?.b;
  if (last && !out.has(last) && last.judge?.score != null && last.judge.score < 0.5) out.set(last, `judge scored the reply ${Number(last.judge.score).toFixed(2)}`);
  const firstAt = rows.findIndex((x) => x.change);
  if (!out.size && firstAt >= 0) {
    const x = rows[firstAt];
    const s = x.b || replyOf(x.turn);
    if (s) out.set(s, why(x));
  }
  if (!out.size && last) out.set(last, 'this answer failed: see Root cause');
  if (cause && out.size) {                           // the diagnosis names the cause on the first defect
    const [k, v] = out.entries().next().value;
    out.set(k, `${cause} · ${v}`);
  }
  return out;
}

// The earliest step, in conversation order, where the new version went wrong: the critical point.
export function criticalOf(rows, defects) {
  if (!defects || !defects.size) return null;
  for (const x of rows) if (x.b && defects.has(x.b)) return x.b;
  return defects.keys().next().value;
}
